import { createContext, useContext, useEffect, useRef } from 'react'

// A tab to open in the content area. `id` is unique: singleton views use a fixed
// id (= component), record forms use '<type>-<id>' so the same record reuses its tab.
export interface TabSpec {
    id: string
    name: string
    component: string
    config?: Record<string, unknown>
}

export interface TabsContextValue {
    openTab: (spec: TabSpec) => void
    closeTab: (id: string) => void
    // Rename an open tab in place (same id, not selected, nothing opened). No-op if absent.
    renameTab: (id: string, name: string) => void
    // Local-only refresh signal: bumps versions[resource] and (if id)
    // versions['<resource>:<id>']. Used by the socket 'data:changed' handler.
    notifyChange: (resource: string, id?: string) => void
    // Save → tell other clients via socket AND refresh own lists. Forms call this
    // after a successful create/update so every open client stays in sync.
    publishChange: (resource: string, id?: string) => void
    versions: Record<string, number>
    // Detail forms register their unsaved-changes state + a save() here so the
    // layout can mark the tab (●) and guard closing. See useDirtyGuard below.
    registerDirtyGuard: (tabId: string, dirty: boolean, save: () => Promise<SaveResult>) => void
    unregisterDirtyGuard: (tabId: string) => void
}

// Result of a form save: ok=true on success, otherwise an error message the
// close-confirm dialog can show.
export interface SaveResult { ok: boolean, error?: string }

export const TabsContext = createContext<TabsContextValue>({
    openTab: () => {},
    closeTab: () => {},
    renameTab: () => {},
    notifyChange: () => {},
    publishChange: () => {},
    versions: {},
    registerDirtyGuard: () => {},
    unregisterDirtyGuard: () => {},
})

export function useTabs() {
    return useContext(TabsContext)
}

// Snapshot of a form for the dirty comparison. Plain JSON.stringify compares key
// order LITERALLY: in a map with dynamic keys (role capabilities, VAT rates per
// country, log levels per module) an entry that is cleared and then set again
// lands at the END of the object. Same content, different text — and the save
// button stays enabled with nothing left to save. Sorting the keys compares
// meaning instead. Array order is left alone: there it carries meaning.
export function stableJson(value: unknown): string {
    return JSON.stringify(value, (_key, v) => (
        v && typeof v === 'object' && !Array.isArray(v)
            ? Object.fromEntries(Object.entries(v as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
            : v
    ))
}

// Detail forms call this so the layout can mark the tab dirty (●) and prompt to
// save / discard / cancel when the tab (or the window) is closed with unsaved
// changes. `save` must persist and resolve true on success, false on failure.
export function useDirtyGuard(tabId: string | undefined, dirty: boolean, save: () => Promise<SaveResult>) {
    const { registerDirtyGuard, unregisterDirtyGuard } = useContext(TabsContext)
    const saveRef = useRef(save)
    saveRef.current = save // always call the latest save without re-registering
    useEffect(() => {
        if (!tabId) return
        registerDirtyGuard(tabId, dirty, () => saveRef.current())
        return () => unregisterDirtyGuard(tabId)
    }, [tabId, dirty, registerDirtyGuard, unregisterDirtyGuard])
}

// notifyChange(ANY_RESOURCE) counts as a change of every resource. Fired after the socket
// reconnects: a 'data:changed' sent while the tab was cut off (sleep, frozen background tab,
// server restart) never arrives, so every open list has to assume it missed one.
export const ANY_RESOURCE = '*'

// A counter that bumps whenever notifyChange(resource) is called. Put it in a
// list's effect deps to refetch after a related form saves.
export function useDataVersion(resource: string): number {
    const { versions } = useContext(TabsContext)
    return (versions[resource] ?? 0) + (versions[ANY_RESOURCE] ?? 0)
}

// useDataVersion for a form that shows ONE record but must also hear changes that arrive without
// its id (server events carry none, and a credit note changes the invoice it reverses without
// touching it). Holds still while `hold` is true — unsaved edits are never overwritten by a
// reload — and catches up as soon as the form is clean again.
export function useDataVersionUnlessDirty(resource: string, hold: boolean): number {
    const version = useDataVersion(resource)
    const seen = useRef(version)
    if (!hold) seen.current = version
    return seen.current
}
